import { cloneValue, isRecord } from "./preset-internals.js";

export function createPresetRuntimeBindings({
  reportError = (error) => globalThis.reportError?.(error),
} = {}) {
  if (typeof reportError !== "function") {
    throw new TypeError("Preset runtime error reporter must be a function.");
  }

  const bindings = new Map();
  const applications = new Map();

  async function apply(key, projection, cause, { force = false } = {}) {
    const binding = bindings.get(key);
    if (projection.kind !== "ready" || !binding) {
      return projection;
    }
    const revisionId = projection.current.revisionId;
    if (cause === "edit") {
      applications.set(key, { binding, revisionId, pending: null });
      return projection;
    }
    const previous = applications.get(key);
    if (
      !force
      && previous?.binding === binding
      && previous.revisionId === revisionId
    ) {
      const outcome = previous.pending ? await previous.pending : null;
      if (outcome?.error) {
        throw outcome.error;
      }
      return projection;
    }

    let settleApplication;
    const application = {
      binding,
      revisionId,
      pending: new Promise((resolve) => {
        settleApplication = resolve;
      }),
    };
    applications.set(key, application);
    try {
      await binding.applyCurrent(cloneValue(projection.current.payload), {
        cause,
        commandRevision: projection.revision,
      });
      settleApplication({ error: null });
      if (applications.get(key) === application) {
        application.pending = null;
      }
      return projection;
    } catch (error) {
      settleApplication({ error });
      if (applications.get(key) === application) {
        applications.delete(key);
      }
      throw error;
    }
  }

  function bind(key, binding, current = null) {
    if (
      !isRecord(binding)
      || typeof binding.captureCurrent !== "function"
      || typeof binding.applyCurrent !== "function"
    ) {
      throw new TypeError("Preset runtime binding requires captureCurrent and applyCurrent.");
    }
    if (bindings.has(key)) {
      throw new TypeError(`Preset runtime binding already exists for ${key}.`);
    }
    bindings.set(key, binding);
    applications.delete(key);
    if (current?.kind === "ready") {
      void apply(key, current, "load", { force: true }).catch(reportError);
    }
    return () => {
      if (bindings.get(key) === binding) {
        bindings.delete(key);
        applications.delete(key);
      }
    };
  }

  function captureCurrent(key, context) {
    return bindings.get(key)?.captureCurrent(context);
  }

  function dispose() {
    bindings.clear();
    applications.clear();
  }

  return Object.freeze({
    apply,
    bind,
    captureCurrent,
    dispose,
  });
}
